/**
 * RemoteWorkPage — WFH Request and Policy Management
 *
 * Validations added:
 * 1. RequestTab: reason required (min 5), start date not past, day count > 0
 * 2. PolicyTab: limit ranges, allowed_days min 1, weekly ≤ monthly
 * 3. MyWFHTab: window.confirm → ConfirmModal
 * 4. PendingTab: reject reason max 500 chars (kept optional per design)
 *
 * All other tabs (Dashboard, AllRequests, Calendar) unchanged.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal, Form,
    Spinner, Alert, Table, ProgressBar
} from 'react-bootstrap';
import {
    Home, Plus, CheckCircle, XCircle, Clock,
    Calendar, BarChart2, Settings, Users,
    MapPin, ChevronLeft, ChevronRight, RefreshCw,
    Check, X, AlertCircle
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES  = ['super-admin', 'admin', 'hr-manager', 'account-manager'];
const MGR_ROLES = [...HR_ROLES, 'reporting-manager'];
const MONTHS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS      = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const STATUS_CONFIG = {
    pending:   { bg:'warning',   label:'Pending',   icon:<Clock size={12}/> },
    approved:  { bg:'success',   label:'Approved',  icon:<CheckCircle size={12}/> },
    rejected:  { bg:'danger',    label:'Rejected',  icon:<XCircle size={12}/> },
    cancelled: { bg:'secondary', label:'Cancelled', icon:<X size={12}/> },
};

const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y, m-1, dd).toLocaleDateString('en-IN', {
        day:'numeric', month:'short', year:'numeric',
    });
};

const nowYM = () => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth()+1 };
};

// ── Shared Confirm Modal ──────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading, confirmVariant='danger', confirmLabel='Confirm' }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Keep</Button>
            <Button variant={confirmVariant} onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ════════════════════════════════════════════════════════════════════════════
const RemoteWorkPage = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);
    const isMgr    = MGR_ROLES.includes(user?.role);
    const [tab, setTab] = useState(isHR ? 'dashboard' : 'my');

    const hrTabs = [
        { key:'dashboard', label:'Dashboard',    icon:BarChart2 },
        { key:'pending',   label:'Pending',      icon:Clock     },
        { key:'all',       label:'All Requests', icon:Users     },
        { key:'calendar',  label:'Calendar',     icon:Calendar  },
        { key:'policy',    label:'Policy',       icon:Settings  },
    ];
    const empTabs = [
        { key:'my',      label:'My WFH',      icon:Home },
        { key:'request', label:'Request WFH', icon:Plus },
    ];
    const tabs = isHR ? hrTabs : empTabs;

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Home size={22} className="me-2 text-primary"/>Remote Work
                    </h4>
                    <p className="text-muted small mb-0">
                        WFH requests · Approvals · Policy · Attendance integration
                    </p>
                </div>
                {isHR && <WFHTodayBadge/>}
            </div>
            <div className="d-flex gap-1 flex-wrap mb-4">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <Button key={t.key} size="sm"
                            variant={tab===t.key ? 'primary' : 'outline-secondary'}
                            onClick={() => setTab(t.key)}>
                            <Icon size={13} className="me-1"/>{t.label}
                        </Button>
                    );
                })}
                {isHR && (
                    <Button size="sm"
                        variant={tab==='my' ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab('my')}>
                        <Home size={13} className="me-1"/>My WFH
                    </Button>
                )}
            </div>
            {tab==='dashboard' && isHR  && <DashboardTab/>}
            {tab==='pending'   && isMgr && <PendingTab/>}
            {tab==='all'       && isMgr && <AllRequestsTab/>}
            {tab==='calendar'  && isMgr && <CalendarTab/>}
            {tab==='policy'    && isHR  && <PolicyTab/>}
            {tab==='my'                 && <MyWFHTab/>}
            {tab==='request'            && <RequestTab onSuccess={() => setTab('my')}/>}
        </div>
    );
};

// ── WFH Today Badge ───────────────────────────────────────────────────────────
const WFHTodayBadge = () => {
    const [count, setCount] = useState(null);
    useEffect(() => {
        axiosInstance.get('/remotework/today')
            .then(r => setCount((r.data.data||[]).length))
            .catch(()=>{});
    }, []);
    if (count === null) return null;
    return (
        <div className="p-3 rounded-3 text-center"
            style={{ background:'linear-gradient(135deg,#eef2ff,#e0e7ff)', minWidth:120 }}>
            <div style={{ fontSize:28, fontWeight:800, color:'#4f46e5' }}>{count}</div>
            <div className="text-muted small">WFH Today</div>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const DashboardTab = () => {
    const { year, month } = nowYM();
    const [stats,   setStats]   = useState(null);
    const [loading, setLoading] = useState(true);
    const [topUsers, setTop]    = useState([]);
    useEffect(() => {
        Promise.all([
            axiosInstance.get('/remotework/stats', { params:{ year, month } }),
            axiosInstance.get('/remotework/top-users'),
        ]).then(([s, t]) => {
            setStats(s.data.data);
            setTop(t.data.data || []);
        }).catch(()=>{}).finally(()=>setLoading(false));
    }, []);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    if (!stats) return null;
    const { summary={}, trend=[], by_department=[], policy_limit=8 } = stats;
    const approvalRate = summary.total_requests
        ? Math.round((summary.approved/summary.total_requests)*100) : 0;
    return (
        <>
            <Row className="g-3 mb-4">
                {[
                    { label:'Requests This Month', val:summary.total_requests||0, color:'#4f46e5' },
                    { label:'Approved',            val:summary.approved||0,        color:'#059669' },
                    { label:'Pending',             val:summary.pending||0,         color:'#d97706' },
                    { label:'Days Approved',       val:summary.total_days_approved||0, color:'#0891b2' },
                ].map((k, i) => (
                    <Col key={i} xs={6} md={3}>
                        <Card className="border-0 shadow-sm p-4 text-center h-100">
                            <div style={{ fontSize:30, fontWeight:800, color:k.color }}>{k.val}</div>
                            <div className="text-muted small">{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Row className="g-4 mb-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">WFH Trend (last 6 months)</div>
                        {trend.length < 2 ? (
                            <div className="text-muted small">Not enough data yet.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={trend.map(r => ({
                                    name:`${MONTHS[r.month-1]} '${String(r.year).slice(-2)}`,
                                    Approved:r.approved, Pending:r.requests-r.approved,
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                                    <XAxis dataKey="name" tick={{fontSize:11}}/>
                                    <YAxis allowDecimals={false} tick={{fontSize:11}}/>
                                    <Tooltip/>
                                    <Legend/>
                                    <Bar dataKey="Approved" fill="#059669" radius={[3,3,0,0]}/>
                                    <Bar dataKey="Pending"  fill="#d97706" radius={[3,3,0,0]}/>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">Approval Rate</div>
                        <div className="text-center mb-4">
                            <div style={{ fontSize:48, fontWeight:800, color:'#059669' }}>{approvalRate}%</div>
                            <div className="text-muted small">{summary.approved||0} of {summary.total_requests||0} approved</div>
                        </div>
                        <div className="fw-bold small mb-2">By Department</div>
                        {by_department.slice(0,5).map((d, i) => (
                            <div key={i} className="mb-2">
                                <div className="d-flex justify-content-between small mb-1">
                                    <span className="text-truncate">{d.department||'N/A'}</span>
                                    <span className="fw-bold">{d.days}d</span>
                                </div>
                                <ProgressBar now={Math.min(100,(d.days/policy_limit)*100)} style={{height:6}} variant="primary"/>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>
            {topUsers.length > 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">
                        Most Frequent WFH Employees (last 3 months)
                    </Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                <th className="ps-4">Employee</th><th>Dept</th>
                                <th>Requests</th><th className="pe-4">Days Approved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topUsers.map((u, i) => (
                                <tr key={i}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{u.employee_name}</div>
                                        <div className="text-muted" style={{fontSize:10}}>{u.employee_code}</div>
                                    </td>
                                    <td>{u.department||'—'}</td>
                                    <td>{u.total_requests}</td>
                                    <td className="pe-4 fw-bold text-primary">{u.total_days}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// PENDING APPROVALS TAB — unchanged except reject reason max 500 chars
// ════════════════════════════════════════════════════════════════════════════
const PendingTab = () => {
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [rejecting,setRejecting]= useState(null);
    const [reason,   setReason]   = useState('');
    const load = () => {
        setLoading(true);
        axiosInstance.get('/remotework/pending')
            .then(r => setRequests(r.data.data||[]))
            .catch(()=>toast.error('Failed to load'))
            .finally(()=>setLoading(false));
    };
    useEffect(load,[]);
    const approve = async (id) => {
        try { await axiosInstance.post(`/remotework/approve/${id}`); toast.success('Approved ✓'); load(); }
        catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };
    const reject = async () => {
        try {
            await axiosInstance.post(`/remotework/reject/${rejecting}`, { reason });
            toast.success('Rejected'); setRejecting(null); setReason(''); load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
    };
    const bulkApprove = async () => {
        if (!selected.size) return;
        try {
            const res = await axiosInstance.post('/remotework/bulk-approve', { ids:[...selected] });
            toast.success(res.data.message); setSelected(new Set()); load();
        } catch { toast.error('Failed'); }
    };
    const toggleSelect = (id) => {
        const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
    };
    if (loading) return <div className="text-center py-5"><Spinner animation="border"/></div>;
    return (
        <>
            {requests.length > 0 && (
                <div className="d-flex gap-2 mb-3 align-items-center">
                    <span className="text-muted small">{requests.length} pending</span>
                    {selected.size > 0 && (
                        <Button size="sm" variant="success" onClick={bulkApprove}>
                            <Check size={13} className="me-1"/>Approve {selected.size} selected
                        </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={load}>
                        <RefreshCw size={13}/>
                    </Button>
                </div>
            )}
            {requests.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <CheckCircle size={40} className="mx-auto mb-2 text-success opacity-60"/>
                    <p>No pending WFH requests. You're all caught up!</p>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                <th className="ps-3">
                                    <Form.Check checked={selected.size===requests.length&&requests.length>0}
                                        onChange={()=>setSelected(selected.size===requests.length?new Set():new Set(requests.map(r=>r.id)))}/>
                                </th>
                                <th>Employee</th><th>Date(s)</th><th>Days</th>
                                <th>Month Used</th><th>Reason</th><th className="pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(r => (
                                <tr key={r.id}>
                                    <td className="ps-3"><Form.Check checked={selected.has(r.id)} onChange={()=>toggleSelect(r.id)}/></td>
                                    <td>
                                        <div className="fw-medium">{r.employee_name}</div>
                                        <div className="text-muted" style={{fontSize:10}}>{r.employee_code} · {r.department}</div>
                                    </td>
                                    <td>
                                        <div>{fmtDate(r.request_date)}</div>
                                        {r.request_date!==r.end_date&&<div className="text-muted" style={{fontSize:10}}>→ {fmtDate(r.end_date)}</div>}
                                    </td>
                                    <td><Badge bg="light" text="dark">{r.days_count}d</Badge></td>
                                    <td><span className="text-muted">{r.month_used||0} used</span></td>
                                    <td className="text-muted" style={{maxWidth:160}}><div className="text-truncate">{r.reason||'—'}</div></td>
                                    <td className="pe-4">
                                        <div className="d-flex gap-1">
                                            <Button size="sm" variant="success" className="py-0" onClick={()=>approve(r.id)}><Check size={13}/></Button>
                                            <Button size="sm" variant="outline-danger" className="py-0" onClick={()=>setRejecting(r.id)}><X size={13}/></Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            <Modal show={!!rejecting} onHide={()=>{setRejecting(null);setReason('');}} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">Reject WFH Request</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Label className="small fw-bold d-flex justify-content-between">
                        <span>Reason <span className="text-muted fw-normal">(optional)</span></span>
                        {reason.length > 400 && (
                            <span className={reason.length>490?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {reason.length}/500
                            </span>
                        )}
                    </Form.Label>
                    <Form.Control as="textarea" rows={3} maxLength={500}
                        placeholder="Let the employee know why…"
                        value={reason} onChange={e=>setReason(e.target.value)}/>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={()=>{setRejecting(null);setReason('');}}>Cancel</Button>
                    <Button variant="danger" onClick={reject}>Reject Request</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// ALL REQUESTS TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const AllRequestsTab = () => {
    const { year, month } = nowYM();
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [filters,  setFilters]  = useState({ year, month:String(month), status:'' });
    const load = () => {
        setLoading(true);
        axiosInstance.get('/remotework/all', {params:filters})
            .then(r=>setRequests(r.data.data||[]))
            .catch(()=>toast.error('Failed'))
            .finally(()=>setLoading(false));
    };
    useEffect(load,[filters]);
    return (
        <>
            <div className="d-flex gap-2 mb-3 flex-wrap">
                <Form.Select size="sm" style={{width:130}} value={filters.month}
                    onChange={e=>setFilters(p=>({...p,month:e.target.value}))}>
                    {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{width:100}} value={filters.year}
                    onChange={e=>setFilters(p=>({...p,year:e.target.value}))}>
                    {[year,year-1,year-2].map(y=><option key={y} value={y}>{y}</option>)}
                </Form.Select>
                <Form.Select size="sm" style={{width:130}} value={filters.status}
                    onChange={e=>setFilters(p=>({...p,status:e.target.value}))}>
                    <option value="">All statuses</option>
                    {Object.entries(STATUS_CONFIG).map(([k,v])=>(
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </Form.Select>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border"/></div> : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                <th className="ps-4">Employee</th><th>Dates</th><th>Days</th>
                                <th>Status</th><th>Reviewed By</th><th className="pe-4">Attendance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length===0&&<tr><td colSpan={6} className="text-center text-muted py-4">No requests found.</td></tr>}
                            {requests.map(r=>{
                                const sc=STATUS_CONFIG[r.status]||STATUS_CONFIG.pending;
                                return <tr key={r.id}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{r.employee_name}</div>
                                        <div className="text-muted" style={{fontSize:10}}>{r.employee_code} · {r.department}</div>
                                    </td>
                                    <td>
                                        <div>{fmtDate(r.request_date)}</div>
                                        {r.request_date!==r.end_date&&<div className="text-muted" style={{fontSize:10}}>→ {fmtDate(r.end_date)}</div>}
                                    </td>
                                    <td>{r.days_count}d</td>
                                    <td><Badge bg={sc.bg} className="d-flex align-items-center gap-1" style={{width:'fit-content'}}>{sc.icon}{sc.label}</Badge></td>
                                    <td className="text-muted">{r.reviewed_by_name||'—'}</td>
                                    <td className="pe-4">
                                        {r.status==='approved'&&(
                                            <Badge bg={r.attendance_marked?'success':'light'} text={r.attendance_marked?'white':'dark'} style={{fontSize:10}}>
                                                {r.attendance_marked?'✓ Marked':'Not marked'}
                                            </Badge>
                                        )}
                                    </td>
                                </tr>;
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// CALENDAR TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const CalendarTab = () => {
    const now   = new Date();
    const [year,  setYear]  = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth()+1);
    const [data,  setData]  = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        axiosInstance.get('/remotework/calendar', {params:{year,month}})
            .then(r=>setData(r.data.data)).catch(()=>{}).finally(()=>setLoading(false));
    }, [year, month]);
    const prev = () => { if(month===1){setMonth(12);setYear(y=>y-1);}else setMonth(m=>m-1); };
    const next = () => { if(month===12){setMonth(1);setYear(y=>y+1);}else setMonth(m=>m+1); };
    const firstDay = new Date(year, month-1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for(let i=0;i<firstDay;i++) cells.push(null);
    for(let d=1;d<=daysInMonth;d++) cells.push(d);
    const wfhByDate = {};
    (data?.requests||[]).forEach(r=>{
        const start=new Date(r.request_date), end=new Date(r.end_date);
        while(start<=end){
            const s=start.toISOString().slice(0,10);
            if(!wfhByDate[s]) wfhByDate[s]=[];
            wfhByDate[s].push(r); start.setDate(start.getDate()+1);
        }
    });
    const todayStr = new Date().toISOString().slice(0,10);
    return (
        <>
            <div className="d-flex align-items-center gap-3 mb-4">
                <Button size="sm" variant="outline-secondary" onClick={prev}><ChevronLeft size={16}/></Button>
                <div className="fw-bold fs-6">{MONTHS[month-1]} {year}</div>
                <Button size="sm" variant="outline-secondary" onClick={next}><ChevronRight size={16}/></Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border"/></div> : (
                <>
                    <Row className="g-0 mb-2">
                        {DAYS.map(d=>(
                            <Col key={d} style={{padding:'4px',textAlign:'center',fontSize:11,color:'#6b7280',fontWeight:600}}>{d}</Col>
                        ))}
                    </Row>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
                        {cells.map((day,i)=>{
                            if(!day) return <div key={i}/>;
                            const dateStr=`${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                            const wfhList=wfhByDate[dateStr]||[];
                            const isToday=dateStr===todayStr;
                            const dow=new Date(year,month-1,day).getDay();
                            const isWeekend=dow===0||dow===6;
                            return (
                                <div key={i} style={{minHeight:80,padding:4,borderRadius:8,
                                    background:isToday?'#eef2ff':isWeekend?'#fafafa':'#fff',
                                    border:`1px solid ${isToday?'#4f46e5':'#e5e7eb'}`}}>
                                    <div style={{fontSize:12,fontWeight:isToday?700:400,
                                        color:isToday?'#4f46e5':isWeekend?'#d1d5db':'#374151',marginBottom:4}}>
                                        {day}
                                    </div>
                                    {wfhList.slice(0,3).map((r,j)=>(
                                        <div key={j} style={{fontSize:9,padding:'1px 4px',borderRadius:3,marginBottom:2,
                                            background:r.status==='approved'?'#d1fae5':'#fef3c7',
                                            color:r.status==='approved'?'#065f46':'#92400e',
                                            overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                                            {r.employee_name?.split(' ')[0]}
                                        </div>
                                    ))}
                                    {wfhList.length>3&&<div style={{fontSize:9,color:'#6b7280'}}>+{wfhList.length-3} more</div>}
                                </div>
                            );
                        })}
                    </div>
                    <div className="d-flex gap-3 mt-3" style={{fontSize:12}}>
                        <div><span style={{display:'inline-block',width:12,height:12,borderRadius:2,background:'#d1fae5',marginRight:4}}/>Approved</div>
                        <div><span style={{display:'inline-block',width:12,height:12,borderRadius:2,background:'#fef3c7',marginRight:4}}/>Pending</div>
                    </div>
                </>
            )}
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// POLICY TAB — VALIDATIONS ADDED
// ════════════════════════════════════════════════════════════════════════════
const validatePolicy = (form) => {
    const e = {};
    const monthly  = parseInt(form.monthly_wfh_limit)    || 0;
    const weekly   = parseInt(form.weekly_wfh_limit)     || 0;
    const consec   = parseInt(form.max_consecutive_days) || 0;
    const advance  = parseInt(form.advance_notice_days);

    if (monthly < 1 || monthly > 31)  e.monthly_wfh_limit    = 'Monthly limit must be between 1 and 31 days';
    if (weekly  < 0 || weekly  > 7)   e.weekly_wfh_limit     = 'Weekly limit must be between 0 and 7 days';
    if (weekly  > monthly)            e.weekly_wfh_limit     = 'Weekly limit cannot exceed monthly limit';
    if (consec  < 1 || consec  > 31)  e.max_consecutive_days = 'Must be between 1 and 31 days';
    if (consec  > monthly)            e.max_consecutive_days = 'Cannot exceed monthly WFH limit';
    if (isNaN(advance) || advance < 0 || advance > 30) e.advance_notice_days = 'Must be between 0 and 30 days';
    if (!form.allowed_days || form.allowed_days.length === 0)
        e.allowed_days = 'Select at least one allowed WFH day';
    return e;
};

const PolicyTab = () => {
    const [form,     setForm]     = useState({});
    const [errors,   setErrors]   = useState({});
    const [attempted,setAttempted]= useState(false);
    const [loading,  setLoading]  = useState(true);
    const [saving,   setSaving]   = useState(false);

    useEffect(() => {
        axiosInstance.get('/remotework/policies/active')
            .then(r => {
                const p = r.data.data;
                setForm(p ? {
                    monthly_wfh_limit:    p.monthly_wfh_limit    || 8,
                    weekly_wfh_limit:     p.weekly_wfh_limit     || 2,
                    max_consecutive_days: p.max_consecutive_days || 3,
                    advance_notice_days:  p.advance_notice_days  || 1,
                    requires_approval:    p.requires_approval !== 0,
                    auto_approve:         !!p.auto_approve,
                    allowed_days: p.allowed_days ? JSON.parse(p.allowed_days) : [1,2,3,4,5],
                } : {
                    monthly_wfh_limit:8, weekly_wfh_limit:2,
                    max_consecutive_days:3, advance_notice_days:1,
                    requires_approval:true, auto_approve:false,
                    allowed_days:[1,2,3,4,5],
                });
            })
            .catch(()=>{}).finally(()=>setLoading(false));
    }, []);

    const setField = (k, v) => {
        setForm(p => ({ ...p, [k]: v }));
        if (attempted) setErrors(prev => { const n={...prev}; delete n[k]; return n; });
    };

    const toggleDay = (d) => {
        const days = form.allowed_days || [];
        const next = days.includes(d) ? days.filter(x=>x!==d) : [...days,d].sort((a,b)=>a-b);
        setField('allowed_days', next);
    };

    const save = async () => {
        setAttempted(true);
        const errs = validatePolicy(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.put('/remotework/settings', form);
            toast.success('WFH policy saved!');
            setAttempted(false);
            setErrors({});
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="text-center py-4"><Spinner animation="border"/></div>;

    return (
        <Card className="border-0 shadow-sm p-4" style={{maxWidth:640}}>
            <h6 className="fw-bold mb-4">
                <Settings size={16} className="me-2 text-primary"/>WFH Policy Configuration
            </h6>
            <Row className="g-4">
                <Col xs={6}>
                    <Form.Label className="small fw-bold">Monthly WFH Limit (days) *</Form.Label>
                    <Form.Control type="number" min={1} max={31}
                        value={form.monthly_wfh_limit||8}
                        isInvalid={attempted&&!!errors.monthly_wfh_limit}
                        onChange={e=>setField('monthly_wfh_limit', parseInt(e.target.value)||0)}/>
                    <Form.Control.Feedback type="invalid">{errors.monthly_wfh_limit}</Form.Control.Feedback>
                    <Form.Text className="text-muted">Max WFH days per employee per month</Form.Text>
                </Col>
                <Col xs={6}>
                    <Form.Label className="small fw-bold">Weekly WFH Limit (days)</Form.Label>
                    <Form.Control type="number" min={0} max={7}
                        value={form.weekly_wfh_limit||0}
                        isInvalid={attempted&&!!errors.weekly_wfh_limit}
                        onChange={e=>setField('weekly_wfh_limit', parseInt(e.target.value)||0)}/>
                    <Form.Control.Feedback type="invalid">{errors.weekly_wfh_limit}</Form.Control.Feedback>
                </Col>
                <Col xs={6}>
                    <Form.Label className="small fw-bold">Max Consecutive Days *</Form.Label>
                    <Form.Control type="number" min={1} max={31}
                        value={form.max_consecutive_days||3}
                        isInvalid={attempted&&!!errors.max_consecutive_days}
                        onChange={e=>setField('max_consecutive_days', parseInt(e.target.value)||1)}/>
                    <Form.Control.Feedback type="invalid">{errors.max_consecutive_days}</Form.Control.Feedback>
                    <Form.Text className="text-muted">Max days in a single request</Form.Text>
                </Col>
                <Col xs={6}>
                    <Form.Label className="small fw-bold">Advance Notice (days) *</Form.Label>
                    <Form.Control type="number" min={0} max={30}
                        value={form.advance_notice_days??1}
                        isInvalid={attempted&&!!errors.advance_notice_days}
                        onChange={e=>setField('advance_notice_days', parseInt(e.target.value)||0)}/>
                    <Form.Control.Feedback type="invalid">{errors.advance_notice_days}</Form.Control.Feedback>
                    <Form.Text className="text-muted">0 = same-day requests allowed</Form.Text>
                </Col>
                <Col xs={12}>
                    <Form.Label className="small fw-bold">
                        Allowed WFH Days *
                        {attempted && errors.allowed_days && (
                            <span className="text-danger ms-2 fw-normal" style={{fontSize:12}}>
                                {errors.allowed_days}
                            </span>
                        )}
                    </Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                        {DAYS.map((d, i) => (
                            <Button key={i} size="sm"
                                variant={(form.allowed_days||[]).includes(i) ? 'primary' : 'outline-secondary'}
                                style={{
                                    minWidth:44,
                                    borderColor: attempted && errors.allowed_days && !(form.allowed_days||[]).includes(i)
                                        ? '#dc3545' : undefined,
                                }}
                                onClick={() => toggleDay(i)}>
                                {d.slice(0,2)}
                            </Button>
                        ))}
                    </div>
                    <Form.Text className="text-muted">WFH is only allowed on selected days</Form.Text>
                </Col>
                <Col xs={12}>
                    <Form.Check type="switch" label="Require manager approval"
                        checked={!!form.requires_approval}
                        onChange={e=>setField('requires_approval', e.target.checked)}
                        className="mb-2"/>
                    <Form.Check type="switch" label="Auto-approve all WFH requests"
                        checked={!!form.auto_approve}
                        onChange={e=>setField('auto_approve', e.target.checked)}/>
                    {form.auto_approve && (
                        <Alert variant="info" className="small py-2 mt-2">
                            Auto-approve will approve requests and mark attendance immediately on submission.
                        </Alert>
                    )}
                </Col>
            </Row>
            <Button variant="primary" className="mt-4" onClick={save} disabled={saving}>
                {saving ? <Spinner size="sm"/> : '💾 Save Policy'}
            </Button>
        </Card>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MY WFH TAB — window.confirm replaced with ConfirmModal
// ════════════════════════════════════════════════════════════════════════════
const MyWFHTab = () => {
    const { year, month } = nowYM();
    const [balance,  setBalance]  = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [confirm,  setConfirm]  = useState({ show:false, id:null, loading:false });

    const load = () => {
        setLoading(true);
        Promise.all([
            axiosInstance.get('/remotework/my/balance', {params:{year,month}}),
            axiosInstance.get('/remotework/my', {params:{year,month}}),
        ]).then(([b,r])=>{ setBalance(b.data.data); setRequests(r.data.data||[]); })
         .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load,[]);

    const handleCancelConfirm = async () => {
        setConfirm(p=>({...p,loading:true}));
        try {
            await axiosInstance.delete(`/remotework/request/${confirm.id}`);
            toast.success('Request cancelled');
            load();
        } catch (err) { toast.error(err.response?.data?.message||'Failed'); }
        finally { setConfirm({show:false,id:null,loading:false}); }
    };

    if (loading) return <div className="text-center py-4"><Spinner animation="border"/></div>;
    const usedPct = balance ? Math.round((balance.used_days/balance.monthly_limit)*100) : 0;
    const pendPct = balance ? Math.round((balance.pending_days/balance.monthly_limit)*100) : 0;

    return (
        <>
            {balance && (
                <Card className="border-0 shadow-sm mb-4 p-4"
                    style={{background:'linear-gradient(135deg,#eef2ff,#e0e7ff)'}}>
                    <div className="fw-bold mb-3">WFH Balance — {MONTHS[month-1]} {year}</div>
                    <Row className="g-3 mb-3">
                        {[
                            {label:'Monthly Limit', val:balance.monthly_limit,  color:'#4f46e5'},
                            {label:'Used',          val:balance.used_days,       color:'#059669'},
                            {label:'Pending',       val:balance.pending_days,    color:'#d97706'},
                            {label:'Remaining',     val:balance.remaining_days,  color:balance.remaining_days===0?'#dc2626':'#374151'},
                        ].map((k,i)=>(
                            <Col key={i} xs={6} md={3}>
                                <div className="text-center bg-white rounded p-3">
                                    <div style={{fontSize:24,fontWeight:800,color:k.color}}>{k.val}</div>
                                    <div className="text-muted small">{k.label}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <div className="mb-1 d-flex justify-content-between small">
                        <span>Monthly usage</span>
                        <span>{balance.used_days+balance.pending_days}/{balance.monthly_limit} days</span>
                    </div>
                    <div style={{height:12,borderRadius:6,background:'#e0e7ff',overflow:'hidden'}}>
                        <div style={{height:'100%',borderRadius:6,
                            background:`linear-gradient(90deg,#4f46e5 ${usedPct}%,#d97706 ${usedPct}% ${usedPct+pendPct}%,transparent ${usedPct+pendPct}%)`}}/>
                    </div>
                    <div className="d-flex gap-3 mt-2" style={{fontSize:11,color:'#6b7280'}}>
                        <span>■ Used</span>
                        <span style={{color:'#d97706'}}>■ Pending</span>
                    </div>
                </Card>
            )}
            {requests.length===0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <Home size={36} className="mx-auto mb-2 opacity-40"/>
                    <p>No WFH requests this month.</p>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:10}}>
                                <th className="ps-4">Date(s)</th><th>Days</th>
                                <th>Status</th><th>Reason</th><th className="pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(r=>{
                                const sc=STATUS_CONFIG[r.status]||STATUS_CONFIG.pending;
                                return <tr key={r.id}>
                                    <td className="ps-4">
                                        <div className="fw-medium">{fmtDate(r.request_date)}</div>
                                        {r.request_date!==r.end_date&&<div className="text-muted" style={{fontSize:10}}>→ {fmtDate(r.end_date)}</div>}
                                    </td>
                                    <td>{r.days_count}d</td>
                                    <td>
                                        <Badge bg={sc.bg} className="d-flex align-items-center gap-1" style={{width:'fit-content'}}>
                                            {sc.icon}{sc.label}
                                        </Badge>
                                        {r.rejection_reason&&<div className="text-danger mt-1" style={{fontSize:10}}>{r.rejection_reason}</div>}
                                    </td>
                                    <td className="text-muted">{r.reason||'—'}</td>
                                    <td className="pe-4">
                                        {r.status==='pending'&&(
                                            <Button size="sm" variant="outline-danger" className="py-0"
                                                onClick={()=>setConfirm({show:true,id:r.id,loading:false})}>
                                                Cancel
                                            </Button>
                                        )}
                                    </td>
                                </tr>;
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}
            <ConfirmModal
                show={confirm.show}
                title="Cancel WFH Request"
                message="Are you sure you want to cancel this WFH request?"
                onConfirm={handleCancelConfirm}
                onCancel={()=>setConfirm({show:false,id:null,loading:false})}
                loading={confirm.loading}
                confirmVariant="danger"
                confirmLabel="Yes, Cancel Request"/>
        </>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// REQUEST WFH TAB — VALIDATIONS ADDED
// ════════════════════════════════════════════════════════════════════════════
const validateRequest = (form, dayCount) => {
    const e = {};
    const today = new Date(); today.setHours(0,0,0,0);

    if (!form.request_date) {
        e.request_date = 'Start date is required';
    } else {
        const d = new Date(form.request_date);
        if (d < today) e.request_date = 'Start date cannot be in the past';
    }

    if (form.request_date && form.end_date) {
        const start = new Date(form.request_date);
        const end   = new Date(form.end_date);
        if (end < start) e.end_date = 'End date cannot be before start date';
        else {
            const spanDays = (end - start) / (1000*60*60*24) + 1;
            if (spanDays > 30) e.end_date = 'A single WFH request cannot span more than 30 days';
        }
    }

    if (dayCount === 0 && form.request_date && form.end_date && !e.request_date && !e.end_date)
        e.dayCount = 'All selected dates are weekends — please choose working days';

    const reason = form.reason?.trim();
    if (!reason)              e.reason = 'Please provide a reason for the WFH request';
    else if (reason.length < 5)  e.reason = 'Reason must be at least 5 characters';
    else if (reason.length > 500) e.reason = 'Reason cannot exceed 500 characters';

    if (form.work_plan && form.work_plan.length > 500) e.work_plan = 'Work plan cannot exceed 500 characters';

    return e;
};

const RequestTab = ({ onSuccess }) => {
    const todayStr    = new Date().toISOString().slice(0,10);
    const tomorrow    = new Date(); tomorrow.setDate(tomorrow.getDate()+1);
    const tomorrowStr = tomorrow.toISOString().slice(0,10);
    const maxDate     = new Date(); maxDate.setMonth(maxDate.getMonth()+3);
    const maxDateStr  = maxDate.toISOString().slice(0,10);

    const [form,       setFormState] = useState({
        request_date: tomorrowStr, end_date: tomorrowStr,
        reason: '', work_plan: '',
    });
    const [balance,   setBalance]   = useState(null);
    const [policy,    setPolicy]    = useState(null);
    const [submitting,setSubmit]    = useState(false);
    const [dayCount,  setDayCount]  = useState(1);
    const [errors,    setErrors]    = useState({});
    const [attempted, setAttempted] = useState(false);

    useEffect(() => {
        const {year,month} = nowYM();
        Promise.all([
            axiosInstance.get('/remotework/my/balance',{params:{year,month}}),
            axiosInstance.get('/remotework/policies/active'),
        ]).then(([b,p])=>{ setBalance(b.data.data); setPolicy(p.data.data); }).catch(()=>{});
    }, []);

    useEffect(() => {
        const start = new Date(form.request_date);
        const end   = new Date(form.end_date);
        if (end < start) { setFormState(p=>({...p,end_date:form.request_date})); setDayCount(1); return; }
        let count = 0;
        const d = new Date(start);
        while(d <= end){ const day=d.getDay(); if(day!==0&&day!==6) count++; d.setDate(d.getDate()+1); }
        setDayCount(count);
    }, [form.request_date, form.end_date]);

    const setField = (k, v) => {
        setFormState(p=>({...p,[k]:v}));
        if(attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; delete n.dayCount; return n; });
    };

    const submit = async (e) => {
        e.preventDefault();
        setAttempted(true);
        const errs = validateRequest(form, dayCount);
        if(Object.keys(errs).length>0){ setErrors(errs); return; }
        setSubmit(true);
        try {
            const res = await axiosInstance.post('/remotework/request', form);
            toast.success(res.data.message);
            onSuccess();
        } catch(err){ toast.error(err.response?.data?.message||'Request failed'); }
        finally{ setSubmit(false); }
    };

    const remaining = balance?.remaining_days ?? null;
    const overLimit = remaining !== null && dayCount > remaining;
    const overConsec = policy?.max_consecutive_days && dayCount > policy.max_consecutive_days;
    const reasonLen = form.reason?.length || 0;

    return (
        <Card className="border-0 shadow-sm p-4" style={{maxWidth:580}}>
            <h6 className="fw-bold mb-4">
                <Home size={16} className="me-2 text-primary"/>Request Work From Home
            </h6>
            {balance && (
                <Alert variant={overLimit?'danger':'info'} className="small py-2 mb-4">
                    This month: <strong>{balance.used_days}</strong> used ·
                    <strong> {balance.pending_days}</strong> pending ·
                    <strong> {remaining}</strong> remaining
                    {policy && ` (limit: ${policy.monthly_wfh_limit}/month)`}
                </Alert>
            )}
            <Form onSubmit={submit} noValidate>
                <Row className="g-3">
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Start Date *</Form.Label>
                        <Form.Control type="date"
                            value={form.request_date}
                            min={todayStr}
                            max={maxDateStr}
                            isInvalid={attempted&&!!errors.request_date}
                            onChange={e=>setField('request_date',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.request_date}</Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">End Date</Form.Label>
                        <Form.Control type="date"
                            value={form.end_date}
                            min={form.request_date||todayStr}
                            max={maxDateStr}
                            isInvalid={attempted&&!!errors.end_date}
                            onChange={e=>setField('end_date',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.end_date}</Form.Control.Feedback>
                    </Col>

                    {/* Day count preview */}
                    <Col xs={12}>
                        <div className="p-3 rounded text-center"
                            style={{background: overLimit||overConsec||errors.dayCount ? '#fff1f2' : '#f0fdf4'}}>
                            <span style={{fontSize:24,fontWeight:800,
                                color:overLimit||overConsec||errors.dayCount?'#dc2626':'#059669'}}>
                                {dayCount}
                            </span>
                            <span className="text-muted ms-2">
                                working day{dayCount!==1?'s':''}
                            </span>
                            {errors.dayCount && (
                                <div className="text-danger small mt-1">
                                    <AlertCircle size={13} className="me-1"/>{errors.dayCount}
                                </div>
                            )}
                            {overLimit && !errors.dayCount && (
                                <div className="text-danger small mt-1">
                                    <AlertCircle size={13} className="me-1"/>
                                    Exceeds remaining balance ({remaining} days)
                                </div>
                            )}
                            {overConsec && !errors.dayCount && (
                                <div className="text-danger small mt-1">
                                    <AlertCircle size={13} className="me-1"/>
                                    Exceeds max consecutive days ({policy.max_consecutive_days})
                                </div>
                            )}
                        </div>
                    </Col>

                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Reason * <span className="text-muted fw-normal">(min 5 characters)</span></span>
                            <span className={reasonLen>450?'text-danger':'text-muted'} style={{fontSize:11}}>
                                {reasonLen}/500
                            </span>
                        </Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={500}
                            placeholder="Brief reason for WFH (required)…"
                            value={form.reason}
                            isInvalid={attempted&&!!errors.reason}
                            onChange={e=>setField('reason',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
                    </Col>

                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Work Plan <span className="text-muted fw-normal">(optional)</span></span>
                            {form.work_plan?.length > 400 && (
                                <span className={form.work_plan.length>490?'text-danger':'text-muted'} style={{fontSize:11}}>
                                    {form.work_plan.length}/500
                                </span>
                            )}
                        </Form.Label>
                        <Form.Control as="textarea" rows={3} maxLength={500}
                            placeholder="What tasks will you work on? Any meetings?"
                            value={form.work_plan}
                            isInvalid={attempted&&!!errors.work_plan}
                            onChange={e=>setField('work_plan',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.work_plan}</Form.Control.Feedback>
                    </Col>
                </Row>

                {policy?.requires_approval && (
                    <Alert variant="light" className="small py-2 mt-3 border">
                        📋 Your request will be sent to your manager for approval.
                    </Alert>
                )}
                {!policy?.requires_approval && (
                    <Alert variant="success" className="small py-2 mt-3">
                        ⚡ WFH requests are auto-approved at your company.
                    </Alert>
                )}

                <Button type="submit" variant="primary" className="mt-3 w-100"
                    disabled={submitting || overLimit || dayCount === 0}>
                    {submitting
                        ? <Spinner size="sm"/>
                        : `🏠 Submit WFH Request (${dayCount} day${dayCount!==1?'s':''})`}
                </Button>
            </Form>
        </Card>
    );
};

export default RemoteWorkPage;
